import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useSchedulingDrawer } from '@web-app/hooks/useSchedulingDrawer';
import { useUpcomingAppointments } from '@web-app/hooks/useUpcomingAppointments';
import {
  resolveTimezone,
  sanitizeHealthieDate,
} from '@web-app/utils/date-time.utils';
import { formatInTimeZone } from 'date-fns-tz';
import { FC } from 'react';
import { LuCalendarDays } from 'react-icons/lu';

import { PrepStepHeader } from './PrepStepHeader';
import { SessionPrepCardSkeleton } from './SessionPrepCardSkeleton';

interface AppointmentStatusCardProps {
  stepNumber?: number;
  /** Healthie patient ID. */
  patientId: string | null;
  /** Patient display name for the scheduling drawer header. */
  patientName: string;
  /**
   * Appointment ID to exclude from the "upcoming" list (the current
   * in-session appointment). Null when used outside a session.
   */
  excludeAppointmentId?: string | null;
}

export const AppointmentStatusCard: FC<AppointmentStatusCardProps> = ({
  stepNumber,
  patientId,
  patientName,
  excludeAppointmentId,
}) => {
  const { appointmentStatus } = useUpcomingAppointments(patientId, {
    excludeAppointmentId,
  });
  const { openSchedule, openReschedule } = useSchedulingDrawer();

  if (appointmentStatus.type === 'loading') {
    return (
      <SessionPrepCardSkeleton
        rows={3}
        data-testid="appointment-status-skeleton"
      />
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {stepNumber !== undefined && (
        <PrepStepHeader
          stepNumber={stepNumber}
          title="Schedule your next session"
          description="Book with your patient before the end of this session"
        />
      )}
      {appointmentStatus.type === 'error' && (
        <Box data-testid="appointment-status-error" py={2}>
          <Text fontSize="sm" color="text.muted" py={4}>
            Failed to load appointment status. Please try again.
          </Text>
        </Box>
      )}
      {appointmentStatus.type === 'no-appointment' && (
        <Box
          bg="bg.default"
          borderRadius="2xl"
          border="1px solid"
          borderColor="border.default"
          p={5}
          data-testid="appointment-status-book"
        >
          <HStack spacing={3} align="center">
            <Icon
              as={LuCalendarDays}
              boxSize="26px"
              color="interactive.brand.default"
              opacity={0.6}
            />
            <Text flex={1} fontSize="sm" fontWeight="600">
              Book a follow up appointment
            </Text>
            <Button
              size="xs"
              onClick={() =>
                patientId &&
                openSchedule(patientId, patientName, { side: 'left' })
              }
            >
              Book now
            </Button>
          </HStack>
        </Box>
      )}
      {appointmentStatus.type === 'scheduled' && (
        <Box
          bg="bg.default"
          borderRadius="2xl"
          border="1px solid"
          borderColor="border.default"
          p={5}
          data-testid="appointment-status-scheduled"
        >
          <HStack spacing={3} align="center">
            <Icon
              as={LuCalendarDays}
              boxSize="26px"
              color="interactive.brand.default"
              opacity={0.6}
            />
            <VStack flex={1} spacing={0} align="start">
              <Text fontSize="sm" fontWeight="600">
                {formatInTimeZone(
                  sanitizeHealthieDate(appointmentStatus.appointment.start),
                  resolveTimezone(),
                  "MMMM d, yyyy 'at' h:mma zzz",
                )}
              </Text>
              <Text fontSize="xs" color="text.muted">
                {appointmentStatus.appointment.appointmentType.name}
              </Text>
            </VStack>
            <Button
              size="xs"
              variant="outline"
              onClick={() =>
                patientId &&
                openReschedule(
                  patientId,
                  patientName,
                  appointmentStatus.appointment,
                  { side: 'left' },
                )
              }
            >
              Change
            </Button>
          </HStack>
        </Box>
      )}
    </VStack>
  );
};

AppointmentStatusCard.displayName = 'AppointmentStatusCard';
